import { Suspense, lazy, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { InboxChatModal } from '~/features/chat/inbox-chat-modal';
import { documentToFeedItem, newChatAsset, sessionToModalAsset } from '~/features/chat/modal-asset';
import { CodingSessionModal } from '~/features/coding/coding-session-modal';
import { isHomeRoute } from '~/lib/chat-origin';
import { chatKeys } from '~/lib/query-keys';
import { useChatOpenStore } from '~/lib/stores/chat-open-store';
import { useCodingOpenStore } from '~/lib/stores/coding-open-store';
import { shouldReturnToOrigin } from '~/lib/stores/window-rules';
import { SETTINGS_WINDOW_ID, SYSTEM_SETTINGS_WINDOW_ID } from './window-titles';
import { useWindowStore } from '~/lib/stores/window-store';
import {
  openBuildWindow,
  openChatAssetWindow,
  pruneWindowPayloads,
  retargetChatWindow,
  useWindowPayloads,
} from './window-payloads';

/**
 * FRONT-3146: the one host for every chat, document, file and Build window
 * opened through the app's open requests (sidebar, briefing rows, command
 * palette, top bar, deep links, voice). Mounted once in the app shell
 * (`layouts/app-layout.tsx`, inside `ChatProvider`), so a window survives page
 * navigation and several can coexist — one `InboxChatModal` instance per chat
 * window, keyed by window id, each with its own state and its own stream.
 *
 * It replaces the Home-only `ChatLauncher` and the coding-request effect in
 * `home.tsx`: the three open stores keep their public API, but their one-shot
 * requests are consumed here and become windows in `window-store.ts`.
 */
export function WindowHost() {
  useConsumeOpenRequests();
  const windows = useWindowStore((s) => s.windows);
  const chat = useWindowPayloads((s) => s.chat);
  const build = useWindowPayloads((s) => s.build);
  const file = useWindowPayloads((s) => s.file);
  const app = useWindowPayloads((s) => s.app);

  useEffect(() => {
    pruneWindowPayloads();
  }, [windows]);

  return (
    <>
      {windows.map((w) => {
        if (w.kind === 'chat' && chat[w.id]) return <HostedChat key={w.id} id={w.id} />;
        if (w.kind === 'build' && build[w.id]) return <HostedBuild key={w.id} id={w.id} />;
        if (w.kind === 'file' && file[w.id]) return <HostedFile key={w.id} id={w.id} />;
        if (w.kind === 'app' && app[w.id]) return <HostedApp key={w.id} id={w.id} />;
        if (w.kind === 'settings') return <HostedSettings key={w.id} />;
        if (w.kind === 'system-settings') return <HostedSystemSettings key={w.id} />;
        return null;
      })}
    </>
  );
}

/** Turns the three stores' one-shot requests into windows, clearing each the instant it is read. */
function useConsumeOpenRequests(): void {
  const chatRequest = useChatOpenStore((s) => s.openRequest);
  const clearChat = useChatOpenStore((s) => s.clearOpenChat);
  useEffect(() => {
    if (!chatRequest) return;
    // FRONT-1453: resolve the session id into whatever asset it names.
    openChatAssetWindow(
      sessionToModalAsset(chatRequest.sessionId, chatRequest.title, new Date().toISOString(), chatRequest.directAgent),
      chatRequest.returnTo,
    );
    clearChat();
  }, [chatRequest, clearChat]);

  const docRequest = useChatOpenStore((s) => s.openDocumentRequest);
  const clearDoc = useChatOpenStore((s) => s.clearOpenDocument);
  useEffect(() => {
    if (!docRequest) return;
    openChatAssetWindow(documentToFeedItem(docRequest.doc), docRequest.returnTo);
    clearDoc();
  }, [docRequest, clearDoc]);

  const codingRequest = useCodingOpenStore((s) => s.openRequest);
  const clearCoding = useCodingOpenStore((s) => s.clearOpenCoding);
  useEffect(() => {
    if (!codingRequest) return;
    openBuildWindow(codingRequest.sessionId, codingRequest.returnTo, codingRequest.fresh === true);
    clearCoding();
  }, [codingRequest, clearCoding]);
}

/** Closes a hosted window and, when it was the last one and the person is still on home, goes back to where it was opened from. */
function useCloseHostedWindow(id: string, returnTo: string | null) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return useCallback(
    (navigateBack: boolean) => {
      useWindowStore.getState().close(id);
      qc.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
      if (!navigateBack) return;
      const remaining = useWindowStore.getState().windows.length;
      if (shouldReturnToOrigin({ returnTo, currentPathIsHome: isHomeRoute(window.location.pathname), remainingWindows: remaining })) {
        navigate(returnTo as string, { replace: true });
      }
    },
    [id, returnTo, navigate, qc],
  );
}

function HostedChat({ id }: { id: string }) {
  const payload = useWindowPayloads((s) => s.chat[id]);
  const qc = useQueryClient();
  const close = useCloseHostedWindow(id, payload?.returnTo ?? null);
  const onClose = useCallback(() => close(true), [close]);
  const onCloseAfterNavigate = useCallback(() => close(false), [close]);
  const onNewChat = useCallback(() => {
    retargetChatWindow(id, newChatAsset());
    // The thread row is minted server-side on the first message; nudge the thread queries.
    window.setTimeout(() => qc.invalidateQueries({ queryKey: chatKeys.threadsRoot() }), 1500);
  }, [id, qc]);
  if (!payload) return null;
  return (
    <InboxChatModal
      item={payload.item}
      windowId={id}
      onClose={onClose}
      onCloseAfterNavigate={onCloseAfterNavigate}
      onNewChat={onNewChat}
    />
  );
}

function HostedBuild({ id }: { id: string }) {
  const payload = useWindowPayloads((s) => s.build[id]);
  const close = useCloseHostedWindow(id, payload?.returnTo ?? null);
  const onClose = useCallback(() => close(true), [close]);
  if (!payload) return null;
  return (
    <CodingSessionModal windowId={id} initialSessionId={payload.sessionId} startFresh={payload.fresh} onClose={onClose} />
  );
}

// FRONT-3171: lazy — PDF.js and the viewer load only when a file is opened.
const FileWindow = lazy(() => import('./file-window').then((m) => ({ default: m.FileWindow })));

function HostedFile({ id }: { id: string }) {
  const payload = useWindowPayloads((s) => s.file[id]);
  const onClose = useCallback(() => useWindowStore.getState().close(id), [id]);
  if (!payload) return null;
  return (
    <Suspense fallback={null}>
      <FileWindow windowId={id} source={payload.source} onClose={onClose} />
    </Suspense>
  );
}

const AppWindow = lazy(() => import('./app-window').then((m) => ({ default: m.AppWindow })));

function HostedApp({ id }: { id: string }) {
  const payload = useWindowPayloads((s) => s.app[id]);
  const title = useWindowStore((s) => s.windows.find((w) => w.id === id)?.title) ?? 'App';
  const onClose = useCallback(() => useWindowStore.getState().close(id), [id]);
  if (!payload) return null;
  return (
    <Suspense fallback={null}>
      <AppWindow windowId={id} slug={payload.slug} url={payload.url} title={title} onClose={onClose} />
    </Suspense>
  );
}

// Lazy: the Settings panels are heavy and most sessions never open the window.
const SettingsWindow = lazy(() => import('~/features/settings/settings-window').then((m) => ({ default: m.SettingsWindow })));

function HostedSettings() {
  const onClose = useCallback(() => useWindowStore.getState().close(SETTINGS_WINDOW_ID), []);
  return (
    <Suspense fallback={null}>
      <SettingsWindow onClose={onClose} />
    </Suspense>
  );
}

const SystemSettingsWindow = lazy(() =>
  import('~/features/settings/settings-window').then((m) => ({ default: m.SystemSettingsWindow })),
);

function HostedSystemSettings() {
  const onClose = useCallback(() => useWindowStore.getState().close(SYSTEM_SETTINGS_WINDOW_ID), []);
  return (
    <Suspense fallback={null}>
      <SystemSettingsWindow onClose={onClose} />
    </Suspense>
  );
}
